/**
 * Fund ledger helpers (NOS-168): admin-fee math (mirrors SQL
 * compute_admin_fee_cents), running balances, and the three CSV exports.
 * Pure functions — unit-tested to 100% on the fee math.
 */
import { escapeCSVField, formatCurrencyCents } from "@/lib/sponsor"

/**
 * round(amount * pct / 100) on integer cents — plain half-up rounding, the
 * same result as the SQL compute_admin_fee_cents used by the ledger trigger.
 */
export function computeAdminFeeCents(amountCents: number, feePct: number): number {
  return Math.round((amountCents * feePct) / 100)
}

export type LedgerEntryType = "donation" | "admin_fee" | "disbursement" | "adjustment"

export interface FundLedgerEntry {
  id: string
  sponsorship_id: string
  child_org_id: string
  type: LedgerEntryType
  /** Signed cents: donations positive; fees/disbursements negative. */
  amount: number
  donation_id: string | null
  memo: string | null
  created_by: string | null
  created_at: string
}

/** Sum of signed entry amounts. */
export function sumEntries(entries: Pick<FundLedgerEntry, "amount">[]): number {
  return entries.reduce((sum, e) => sum + e.amount, 0)
}

/**
 * Running balance per entry, oldest-first input → same order out.
 */
export function withRunningBalance<T extends Pick<FundLedgerEntry, "amount">>(
  entries: T[]
): (T & { runningBalanceCents: number })[] {
  let balance = 0
  return entries.map((e) => {
    balance += e.amount
    return { ...e, runningBalanceCents: balance }
  })
}

export const ENTRY_TYPE_LABELS: Record<LedgerEntryType, string> = {
  donation: "Donation",
  admin_fee: "Admin fee",
  disbursement: "Disbursement",
  adjustment: "Adjustment",
}

/** (a) Per-project ledger CSV: date, type, memo, amount, running balance. */
export function buildLedgerCsv(entriesOldestFirst: FundLedgerEntry[]): string {
  const headers = ["Date", "Type", "Memo", "Amount", "Balance"]
  const rows = withRunningBalance(entriesOldestFirst).map((e) => [
    new Date(e.created_at).toISOString().split("T")[0],
    ENTRY_TYPE_LABELS[e.type],
    escapeCSVField(e.memo ?? ""),
    (e.amount / 100).toFixed(2),
    (e.runningBalanceCents / 100).toFixed(2),
  ])
  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n")
}

export interface ProjectFundSummary {
  childName: string
  childLegalName: string | null
  childEin: string | null
  childAddress: string | null
  donationsCents: number
  feesCents: number
  disbursementsCents: number
  adjustmentsCents: number
  balanceCents: number
  ytdDonationsCents: number
  ytdDisbursementsCents: number
}

/** Aggregates one project's entries into the rollup/990-prep shape. */
export function summarizeProject(
  entries: FundLedgerEntry[],
  meta: Pick<ProjectFundSummary, "childName" | "childLegalName" | "childEin" | "childAddress">,
  now: Date = new Date()
): ProjectFundSummary {
  const yearStart = new Date(now.getFullYear(), 0, 1)
  const summary: ProjectFundSummary = {
    ...meta,
    donationsCents: 0,
    feesCents: 0,
    disbursementsCents: 0,
    adjustmentsCents: 0,
    balanceCents: 0,
    ytdDonationsCents: 0,
    ytdDisbursementsCents: 0,
  }
  for (const e of entries) {
    summary.balanceCents += e.amount
    const inYtd = new Date(e.created_at) >= yearStart
    switch (e.type) {
      case "donation":
        summary.donationsCents += e.amount
        if (inYtd) summary.ytdDonationsCents += e.amount
        break
      case "admin_fee":
        summary.feesCents += e.amount
        break
      case "disbursement":
        summary.disbursementsCents += e.amount
        if (inYtd) summary.ytdDisbursementsCents += e.amount
        break
      case "adjustment":
        summary.adjustmentsCents += e.amount
        break
    }
  }
  return summary
}

/** (b) Portfolio rollup CSV by project — Form 990 prep shape. */
export function buildPortfolioFundCsv(summaries: ProjectFundSummary[]): string {
  const headers = [
    "Project",
    "Donations (all time)",
    "Admin fees",
    "Disbursements",
    "Adjustments",
    "Balance",
    "Donations YTD",
    "Disbursements YTD",
  ]
  const rows = summaries.map((s) => [
    escapeCSVField(s.childName),
    (s.donationsCents / 100).toFixed(2),
    (s.feesCents / 100).toFixed(2),
    (s.disbursementsCents / 100).toFixed(2),
    (s.adjustmentsCents / 100).toFixed(2),
    (s.balanceCents / 100).toFixed(2),
    (s.ytdDonationsCents / 100).toFixed(2),
    (s.ytdDisbursementsCents / 100).toFixed(2),
  ])
  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n")
}

/**
 * (c) Schedule I-shaped export (Model C): grantee legal name, EIN, address,
 * total re-granted in period (YTD disbursements, shown positive).
 */
export function buildScheduleICsv(summaries: ProjectFundSummary[]): string {
  const headers = ["Grantee Legal Name", "EIN", "Address", "Amount Granted (YTD)"]
  const rows = summaries
    .filter((s) => s.ytdDisbursementsCents !== 0)
    .map((s) => [
      escapeCSVField(s.childLegalName?.trim() || s.childName),
      escapeCSVField(s.childEin ?? ""),
      escapeCSVField(s.childAddress ?? ""),
      (Math.abs(s.ytdDisbursementsCents) / 100).toFixed(2),
    ])
  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n")
}

export function formatSignedCurrency(cents: number): string {
  const formatted = formatCurrencyCents(Math.abs(cents))
  return cents < 0 ? `−${formatted}` : formatted
}
